"use client";

import Link from "next/link";
import { motion, useReducedMotion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { ease, duration } from "@/lib/motion/easings";
import { Button } from "@/components/ui/Button";
import { GAME_URL } from "@/lib/game/tournaments";
import { HeroSceneWrapper } from "./HeroSceneWrapper";

export function HeroSection() {
  const reducedMotion = useReducedMotion();

  const reveal = (delay: number, durationOverride?: number) =>
    reducedMotion
      ? { initial: false, animate: { opacity: 1, y: 0 } }
      : {
          initial: { opacity: 0, y: 16 },
          animate: { opacity: 1, y: 0 },
          transition: {
            delay,
            duration: durationOverride ?? duration.base,
            ease: ease.cinematic,
          },
        };

  return (
    <section className="relative flex h-screen w-full flex-col items-center justify-center overflow-hidden px-6 text-center lg:px-12">
      {/* Single centered group — crown sits directly above the type stack. */}
      <motion.div {...reveal(0.1, 0.8)} className="mx-auto">
        <HeroSceneWrapper className="h-[120px] w-[120px] sm:h-[160px] sm:w-[160px] lg:h-[200px] lg:w-[200px]" />
      </motion.div>

      <motion.p
        {...reveal(0.3, 0.6)}
        className="mt-4 font-body text-xs uppercase tracking-[0.3em] text-gold"
      >
        The Trading Esports League
      </motion.p>

      <motion.h1
        {...reveal(0.5, duration.cinematic)}
        className="mt-4 font-display text-4xl leading-[0.95] tracking-tight text-fg sm:text-5xl lg:text-6xl xl:text-7xl"
      >
        WHERE TRADERS<br />
        BECOME <span className="text-gold">CHAMPIONS</span>
      </motion.h1>

      <motion.p
        {...reveal(0.9, 0.6)}
        className="mt-6 max-w-xl font-body text-base text-fg-muted sm:text-lg"
      >
        Go 1v1, take the daily, or squad up with a friend. Same live chart,
        highest balance wins. All on play money, all decided by skill.
      </motion.p>

      <motion.div {...reveal(1.2, 0.5)} className="mt-8">
        <Button variant="gold-filled" href={GAME_URL}>
          Play Free
        </Button>
      </motion.div>

      <motion.div {...reveal(1.5, 0.5)} className="mt-4">
        <Link
          href="/partners"
          className="font-body text-sm text-fg-muted underline-offset-4 hover:text-gold hover:underline"
        >
          Sponsor or partner with us &rarr;
        </Link>
      </motion.div>

      {/* Chevron — desktop only */}
      <motion.div
        {...reveal(2.2, 0.6)}
        className="absolute bottom-1 left-1/2 z-10 hidden -translate-x-1/2 lg:block"
      >
        <motion.div
          animate={reducedMotion ? undefined : { y: [0, 6, 0] }}
          transition={
            reducedMotion
              ? undefined
              : { duration: 2, repeat: Infinity, ease: "easeInOut" }
          }
          className="text-fg-faint"
        >
          <ChevronDown className="h-4 w-4" aria-hidden />
          <span className="sr-only">Scroll down</span>
        </motion.div>
      </motion.div>
    </section>
  );
}
